/**
 * DSM-5 / DISC-5 diagnostic criteria for the four K-NAMHS targets.
 *
 * Faithful TypeScript port of
 * `MoDN/modn/scripts/experiment/min_questions/criteria.py`, which encodes
 * Appendix A of the K-NAMHS technical manual. Atoms reference K-NAMHS feature
 * names with their *post-cleaning* categorical labels (e.g. "Yes", "Some of the
 * time"), matching `coarse_cleaning` in `modn/datasets/k_namhs.py`.
 *
 * Age-conditional sub-criteria (GAD-C, PTSD-B2) use the stricter (>=12) branch,
 * because the consultation never asks the adolescent's age (see the Python
 * source for the specificity-over-sensitivity rationale).
 */
import {and, atLeast, atom, Atom, Expr, or} from './boolean';

// --- Atom shorthands (mirror criteria.py) ---------------------------------

/** Standard Yes/No item answered "Yes". */
const Yes = (q: string): Atom => atom(q, 'Yes');

/** Impairment item at intermediate or severe level (codes 2 or 3), on either
 * the frequency or the severity scale. */
const Impair = (q: string): Atom =>
  atom(q, 'Some of the time', 'A lot of the time', 'Bad', 'Very bad');

/** PTSD intrusion-style item at any positive level (codes 1 or 2). */
const PtsdPos = (q: string): Atom => atom(q, 'Sometimes/somewhat', 'Yes');

// --- Social phobia (y5soyima) ---------------------------------------------

const SOPH_A = or(
  Yes('ysoq001a'), Yes('ysoq002a'),
  Yes('ysoq003'), Yes('ysoq004'), Yes('ysoq005'), Yes('ysoq006'),
);
const SOPH_B = or(Yes('ysoq007'), Yes('ysoq008'));
const SOPH_C = or(Yes('ysoq009'), Yes('ysoq012'));
const SOPH_D = or(Yes('ysoq011'), Yes('ysoq014'));
const SOPH_E = Yes('ysoq015');
const SOPH_F = Yes('ysoq016');
const SOPH_G = or(
  Impair('ysoq019a'), Impair('ysoq020a'),
  Impair('ysoq021a'), Impair('ysoq022a'),
  Impair('ysoq023a'), Impair('ysoq024a'),
);
const SOPH = and(SOPH_A, SOPH_B, SOPH_C, SOPH_D, SOPH_E, SOPH_F, SOPH_G);

// --- Generalised anxiety disorder (y5gayima) ------------------------------

const GAD_A = and(Yes('ygaq014'), or(Yes('ygaq014a'), Yes('ygaq014b')));
const GAD_B = or(
  Yes('ygaq003b'), Yes('ygaq004b'), Yes('ygaq005b'),
  Yes('ygaq006b'), Yes('ygaq007b'), Yes('ygaq008b'),
  Yes('ygaq009b'), Yes('ygaq010b'), Yes('ygaq011b'),
  Yes('ygaq012b'), Yes('ygaq013c'),
);
const GAD_C1 = Yes('ygaq016a');
const GAD_C2 = Yes('ygaq017a');
const GAD_C3 = or(Yes('ygaq018a'), Yes('ygaq019a'));
const GAD_C4 = Yes('ygaq021a');
const GAD_C5 = Yes('ygaq015a');
const GAD_C6 = Yes('ygaq020a');
// Age-conditional: stricter (>=12) branch -> at least 3 of 6, plus ygaq022.
const GAD_C = and(
  atLeast(3, GAD_C1, GAD_C2, GAD_C3, GAD_C4, GAD_C5, GAD_C6),
  Yes('ygaq022'),
);
const GAD_D = or(
  Impair('ygaq024a'), Impair('ygaq025a'),
  Impair('ygaq026a'), Impair('ygaq027a'),
  Impair('ygaq028a'), Impair('ygaq029a'),
);
const GAD = and(GAD_A, GAD_B, GAD_C, GAD_D);

// --- Major depressive disorder (y5mdyima) ---------------------------------

const MDD_A1 = or(Yes('ymdq001g'), Yes('ymdq003g'));
const MDD_A2 = Yes('ymdq002c');
const MDD_A3 = or(
  Yes('ymdq004d'), Yes('ymdq005b'),
  Yes('ymdq006b'), Yes('ymdq007b'),
);
const MDD_A4 = or(and(Yes('ymdq008a'), Yes('ymdq008c')), Yes('ymdq009b'));
const MDD_A5 = or(
  and(Yes('ymdq010a'), Yes('ymdq010c')),
  and(Yes('ymdq011a'), Yes('ymdq011b'), Yes('ymdq011d')),
);
const MDD_A6 = or(Yes('ymdq012b'), and(Yes('ymdq013a'), Yes('ymdq013c')));
const MDD_A7 = or(
  and(Yes('ymdq015a'), Yes('ymdq015c')),
  and(Yes('ymdq016a'), Yes('ymdq016c')),
);
const MDD_A8 = or(
  Yes('ymdq017b'),
  and(Yes('ymdq018a'), Yes('ymdq018c')),
  and(Yes('ymdq019a'), Yes('ymdq019c')),
);
const MDD_A9 = or(Yes('ymdq021b'), Yes('ymdq022c'), Yes('ymdq023f'));
// At least 5 of A1-A9, with at least one of (A1, A2) — depressed mood/anhedonia.
const MDD_A = and(
  or(MDD_A1, MDD_A2),
  atLeast(5, MDD_A1, MDD_A2, MDD_A3, MDD_A4, MDD_A5, MDD_A6, MDD_A7, MDD_A8, MDD_A9),
);
const MDD_B = or(
  Impair('ymdq033a'), Impair('ymdq034a'),
  Impair('ymdq035a'), Impair('ymdq036a'),
  Impair('ymdq037a'), Impair('ymdq038a'),
);
const MDD = and(MDD_A, MDD_B);

// --- Posttraumatic stress disorder (y5ptyima) -----------------------------

const PTSD_A1 = or(
  Yes('yptq001'), Yes('yptq002'), Yes('yptq003'),
  Yes('yptq004'), Yes('yptq005'), Yes('yptq006'),
  Yes('yptq007'), Yes('yptq010'),
);
const PTSD_A2 = or(Yes('yptq008'), Yes('yptq009'));
const PTSD_A3 = or(Yes('yptq011'), Yes('yptq012'));
const PTSD_A = or(PTSD_A1, PTSD_A2, PTSD_A3);

const PTSD_B1 = or(
  or(and(PtsdPos('yptqy018a'), PtsdPos('yptqy018b')), Yes('yptqy019')),
  or(and(PtsdPos('yptqe018a'), PtsdPos('yptqe018b')), Yes('yptqe019')),
);
// B2: stricter (>=12) branch — sub-items only.
const PTSD_B2 = or(
  PtsdPos('yptqy022a'), PtsdPos('yptqy022b'),
  PtsdPos('yptqe022a'), PtsdPos('yptqe022b'),
);
const PTSD_B3 = or(Yes('yptqy023'), Yes('yptqe023'));
const PTSD_B4 = or(
  or(Yes('yptqy024c'), Yes('yptqy024d')),
  or(Yes('yptqe024c'), Yes('yptqe024d')),
);
const PTSD_B5 = or(Yes('yptqy024a'), Yes('yptqe024a'));
const PTSD_B = and(
  or(PTSD_B1, PTSD_B2, PTSD_B3, PTSD_B4, PTSD_B5),
  Yes('yptq013'),
);

const PTSD_C1 = or(Yes('yptqy025'), Yes('yptqe025'));
const PTSD_C2 = or(
  or(Yes('yptqy026'), Yes('yptqy027'), Yes('yptqy028')),
  or(Yes('yptqe026'), Yes('yptqe027'), Yes('yptqe028')),
);
const PTSD_C = or(PTSD_C1, PTSD_C2);

const PTSD_D1 = or(Yes('yptqy029'), Yes('yptqe029'));
const PTSD_D2 = or(
  or(Yes('yptqy030a'), Yes('yptqy031a')),
  or(Yes('yptqe030a'), Yes('yptqe031a')),
);
const PTSD_D3 = or(
  or(Yes('yptqy032'), and(Yes('yptqy032a'), Yes('yptqy032b'))),
  or(Yes('yptqe032'), and(Yes('yptqe032a'), Yes('yptqe032b'))),
);
const PTSD_D4 = or(Yes('yptqy033a'), Yes('yptqe033a'));
// D5: yptqe038/039 are stems (no `a`), per verbatim Appendix A.
const PTSD_D5 = or(
  or(Yes('yptqy038a'), Yes('yptqy039a')),
  or(Yes('yptqe038'), Yes('yptqe039')),
);
const PTSD_D6 = or(Yes('yptqy040a'), Yes('yptqe040a'));
const PTSD_D7 = or(Yes('yptqy034a'), Yes('yptqe034a'));
const PTSD_D = atLeast(2, PTSD_D1, PTSD_D2, PTSD_D3, PTSD_D4, PTSD_D5, PTSD_D6, PTSD_D7);

const PTSD_E1 = or(
  and(Yes('yptqy043a'), Yes('yptqy044a')),
  and(Yes('yptqe043'), Yes('yptqe044a')),
);
const PTSD_E2 = or(Yes('yptqy046a'), Yes('yptqe046a'));
// E3: asymmetric atoms on the e-branch (verbatim from Appendix A).
const PTSD_E3 = or(
  or(Yes('yptqy048a'), Yes('yptqy049a')),
  or(Yes('yptqe043'), Yes('yptqe044a')),
);
const PTSD_E4 = or(Yes('yptqy050a'), Yes('yptqe050a'));
const PTSD_E5 = or(Yes('yptqy047a'), Yes('yptqe047a'));
const PTSD_E6 = or(
  or(Yes('yptqy020a'), Yes('yptqy021a')),
  or(Yes('yptqe020a'), Yes('yptqe021a')),
);
const PTSD_E = atLeast(2, PTSD_E1, PTSD_E2, PTSD_E3, PTSD_E4, PTSD_E5, PTSD_E6);

const PTSD_F = or(Yes('yptqy051'), Yes('yptqe051'));

const PTSD_G = or(
  Impair('yptqy054a'), Impair('yptqy055a'),
  Impair('yptqy056a'), Impair('yptqy057a'),
  Impair('yptqy058a'), Impair('yptqy059a'),
  Impair('yptqe054a'), Impair('yptqe055a'),
  Impair('yptqe056a'), Impair('yptqe057a'),
  Impair('yptqe058a'), Impair('yptqe059a'),
);

const PTSD = and(PTSD_A, PTSD_B, PTSD_C, PTSD_D, PTSD_E, PTSD_F, PTSD_G);

// --- Public registry -------------------------------------------------------

export const CRITERIA: Record<string, Expr> = {
  y5soyima: SOPH,
  y5gayima: GAD,
  y5mdyima: MDD,
  y5ptyima: PTSD,
};

export const TARGET_LABELS: Record<string, string> = {
  y5soyima: 'SoPh',
  y5gayima: 'GAD',
  y5mdyima: 'MDD',
  y5ptyima: 'PTSD',
};

/** Lay-friendly module titles (from entropy_app/data.py `_MODULE_TITLES`). */
export const MODULE_TITLES: Record<string, string> = {
  y5soyima: 'Social anxiety',
  y5gayima: 'Generalised anxiety',
  y5mdyima: 'Low mood',
  y5ptyima: 'Stress after a difficult event',
};

/** Canonical target order (used to build module specs; Suicide has no DISC-5
 * criteria and is handled separately as a safety screen). */
export const TARGET_ORDER: string[] = ['y5soyima', 'y5gayima', 'y5mdyima', 'y5ptyima'];

/** Fixed order in which the consultation presents the four modules (clinical
 * preference): Low mood → Generalised anxiety → Social anxiety → Stress. */
export const CONSULTATION_MODULE_ORDER: string[] = [
  'y5mdyima',
  'y5gayima',
  'y5soyima',
  'y5ptyima',
];
